import React, { ReactNode } from 'react';

import { differenceInYears, isExists, isFuture, isValid } from 'date-fns';
import { IntlFormatters } from 'react-intl';
import { Link } from 'react-router-dom';

import { REGIONS } from 'state/intl/types';
import { RBIBrand, brand, getCountry } from 'utils/environment';
import { formatDateObject } from 'utils/form';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import formatListOfWords from 'utils/intl/formatListOfWords';

import { LONGEST_RECORDED_HUMAN_LIFE_IN_YEARS, MINIMUM_SIGNUP_AGE } from './constants';

const getBelowMinimumMessage = (formatMessage: IntlFormatters['formatMessage']) => {
  if (brand() === RBIBrand.PLK && getCountry() === 'ch') {
    return formatMessage({ id: 'invalidDateOfBirthProvided' });
  }

  return formatMessage(
    { id: 'dateOfBirthIsBelowMinimum' },
    {
      termsLink: (
        <Link to={formatMessage({ id: 'routes.termsOfService' })}>
          {formatMessage({ id: 'termsOfService' })}
        </Link>
      ),
      privacyPolicyLink: (
        <Link to={formatMessage({ id: 'routes.privacyPolicy' })}>
          {formatMessage({ id: 'privacyPolicy' })}
        </Link>
      ),
    }
  );
};

const checkDob = (
  dobDate: Date,
  dobRawInput: { month: string; day: string; year: string },
  formatMessage: IntlFormatters['formatMessage']
): ReactNode | null => {
  // JS Date object sucks...
  dobDate.setTime(dobDate.getTime() + dobDate.getTimezoneOffset() * 60000);

  if (
    !isValid(dobDate) ||
    !isExists(
      parseInt(dobRawInput.year),
      parseInt(dobRawInput.month) - 1,
      parseInt(dobRawInput.day)
    )
  ) {
    return formatMessage({ id: 'invalidDateOfBirthProvided' });
  }
  if (isFuture(dobDate)) {
    return formatMessage({ id: 'futureDateOfBirthProvided' });
  }
  if (differenceInYears(Date.now(), dobDate) > LONGEST_RECORDED_HUMAN_LIFE_IN_YEARS) {
    return formatMessage({ id: 'invalidDateOfBirthProvided' });
  }
  if (differenceInYears(Date.now(), dobDate) < MINIMUM_SIGNUP_AGE) {
    return getBelowMinimumMessage(formatMessage);
  }

  return null;
};

export const getDobErrors = (
  dob = { month: '', day: '', year: '' },
  formatMessage: IntlFormatters['formatMessage'],
  region: REGIONS /* eslint-disable-line @typescript-eslint/no-unused-vars */ // Required for other brands
): ReactNode | null => {
  const isRequired = (() => {
    const req = validationConfig.dob.required;

    if (req === false) {
      return false;
    }

    return req === true || req.locales.includes(region);
  })();

  if (isRequired && !Object.values(dob).every(Boolean)) {
    return formatMessage({ id: 'dateOfBirthIsRequired' });
  }

  // Empty DOB (valid)
  if (!dob.month && !dob.day && !dob.year) {
    return null; // dob is not required
  }

  // Partial DOB
  if (!dob.month || !dob.day || !dob.year) {
    return getInvalidDateOfBirthMessage(dob, formatMessage);
  }

  return checkDob(new Date(formatDateObject(dob)), dob, formatMessage);
};

export const getLegacyDobErrors = (
  dob: string | undefined,
  formatMessage: IntlFormatters['formatMessage'],
  region: REGIONS /* eslint-disable-line @typescript-eslint/no-unused-vars */ // Required for other brands
): null | ReactNode => {
  // Empty DOB (valid)
  if (!dob) {
    return null; // dob is not required
  }

  const [month, day, year] = dob.split('/');

  return checkDob(new Date(dob), { year, month, day }, formatMessage);
};

function getInvalidDateOfBirthMessage(
  dob = { month: '', day: '', year: '' },
  formatMessage: IntlFormatters['formatMessage']
) {
  let missingFieldList = [];
  if (!dob.year) {
    missingFieldList.push(formatMessage({ id: 'year' }));
  }
  if (!dob.month) {
    missingFieldList.push(formatMessage({ id: 'month' }));
  }
  if (!dob.day) {
    missingFieldList.push(formatMessage({ id: 'day' }));
  }

  const missingFields = formatListOfWords({ formatMessage, list: missingFieldList });
  return formatMessage(
    { id: 'invalidDateMissingFields' },
    {
      count: missingFieldList.length,
      missingFields,
    }
  );
}
